import * as Apollo from '@apollo/react-hooks'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { Moment } from 'moment-timezone'
import * as React from 'react'

import { LOCIZE_NAMESPACE } from 'main/common'
import ChildrenSelector, { IChildEditState } from 'main/components/child/ChildrenSelector'
import Divider from 'main/components/common/Divider'
import DesiredWeeklyScheduleFields, {
  IDesiredWeeklyScheduleParams,
} from 'main/components/family/DesiredWeeklyScheduleFields'
import { IChild } from 'main/gql/child'
import { Button, NativeLink, spacing, Strong, Text, typography } from 'main/ui'
import { DATE_FORMAT } from 'main/util/date'
import { momentFromDateAndTime } from 'main/util/moment'
import { TFunction, useTranslation } from 'react-i18next'
import { i18nKeyPrefix } from '../../../i18n'
import SchoolSummary from '../../../ScheduleTourModal/common/SchoolSummary'
import { ErrorText } from '../../../ScheduleTourModal/steps/Review/ConfirmForm/ConfirmForm.styles'
import { IOpening } from '../../Content'
import {
  CREATE_TOUR_REQUEST_MUTATION,
  REQUEST_TOUR_MODAL_QUERY,
  RequestTourModal_school,
} from '../../gql'
import {
  CreateTourRequest,
  CreateTourRequest_submitTourRequest,
  CreateTourRequestVariables,
} from '../../gql/__generated__/CreateTourRequest'
import { IValidation } from './types'
import { initialChildEditStates, validate } from './util'

const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL-DETAILS`

interface IProps {
  children: IChild[]
  initialSelectedChildren: IChild[]
  school: RequestTourModal_school
  proposedOpenings: IOpening[]
  desiredWeeklyScheduleParams: IDesiredWeeklyScheduleParams
  onChangeDesiredWeeklyScheduleParams: React.Dispatch<
    React.SetStateAction<IDesiredWeeklyScheduleParams>
  >
  onComplete: (mutationResult: CreateTourRequest_submitTourRequest) => void
  onRequestOpeningSelection: () => void
}

export default function Details(props: IProps) {
  const {
    children,
    initialSelectedChildren,
    school,
    proposedOpenings,
    desiredWeeklyScheduleParams,
    onChangeDesiredWeeklyScheduleParams,
    onComplete,
    onRequestOpeningSelection,
  } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [error, setError] = React.useState<string | null>(null)
  const [validation, setValidation] = React.useState<IValidation>({})
  const [existingChildren, setExistingChildren] = React.useState<IChild[]>(children)
  const [selectedChildren, setSelectedChildren] = React.useState<IChild[]>(initialSelectedChildren)
  const [childEditStates, setChildEditStates] = React.useState<IChildEditState[]>(
    initialChildEditStates(children),
  )

  const openings = proposedOpenings.map(opening =>
    momentFromDateAndTime(
      moment(opening.date).format(DATE_FORMAT),
      opening.beginsAt,
      school.timezone,
    ),
  )

  const [createTourRequest, { loading }] = Apollo.useMutation<
    CreateTourRequest,
    CreateTourRequestVariables
  >(CREATE_TOUR_REQUEST_MUTATION, {
    onError: () => setError('Something went wrong! Please try again.'),
    onCompleted: data => handleCompleted(data, onComplete),
    refetchQueries: [{ query: REQUEST_TOUR_MODAL_QUERY, variables: { schoolId: school.id } }],
  })

  return (
    <div>
      <SchoolSummary school={school} />
      {openings.map((opening, idx) => {
        return (
          <div key={idx} css={{ marginTop: spacing.majorScale(2) }}>
            <Strong>{t(`${PRE}-TOURING`)}</Strong>
            <Text>{opening.format('dddd, MMMM D YYYY · h:mma')}&nbsp;&nbsp;</Text>
            <NativeLink
              css={{ fontWeight: typography.fontWeight.bold }}
              onClick={onRequestOpeningSelection}
            >
              {t(`${PRE}-LINK`)}
            </NativeLink>
          </div>
        )
      })}

      <Divider margin={spacing.minorScale(5)} />

      <div css={{ marginBottom: spacing.majorScale(1) }}>
        <Strong>{t(`${PRE}-SCHEDULE-SECTION`)}</Strong>
        <div css={{ marginTop: spacing.majorScale(2) }}>
          <DesiredWeeklyScheduleFields
            params={desiredWeeklyScheduleParams}
            validationHints={validation.desiredWeeklySchedule || {}}
            onChangeDays={days =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                days: { ...currentParams.days, ...days },
              }))
            }
            onChangeDropoffAt={dropoffAt =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                dropoffAt,
              }))
            }
            onChangePickupAt={pickupAt =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                pickupAt,
              }))
            }
          />
        </div>
      </div>

      <Divider margin={spacing.minorScale(5)} />

      <Strong>{t(`${PRE}-CHILD-SECTION`)}</Strong>
      <div css={{ marginTop: spacing.majorScale(2) }}>
        <ChildrenSelector
          children={existingChildren}
          selectedChildren={selectedChildren}
          editStates={childEditStates}
          validationHint={validation.selectedChildren}
          onAddChild={child => {
            setExistingChildren(prev => prev.concat(child))
            setSelectedChildren(prev => prev.concat(child))
          }}
          onUpdateChild={child => {
            setExistingChildren(prev => prev.map(c => (c.id === child.id ? child : c)))
            setSelectedChildren(prev =>
              selectedChildren.find(c => c.id === child.id) ? prev : prev.concat(child),
            )
          }}
          onChangeSelectedChildren={setSelectedChildren}
          onChangeEditStates={setChildEditStates}
        />
      </div>

      {error && (
        <div css={{ marginTop: spacing.majorScale(2) }}>
          <ErrorText>{error}</ErrorText>
        </div>
      )}

      <div css={{ marginTop: spacing.majorScale(4) }}>
        <Button
          full
          onClick={() =>
            handleSubmit(
              createTourRequest,
              desiredWeeklyScheduleParams,
              existingChildren,
              selectedChildren,
              childEditStates,
              school,
              openings,
              setValidation,
              t,
            )
          }
        >
          {loading ? t(`${PRE}-LOADING`) : t(`${PRE}-BUTTON`)}
        </Button>
      </div>
    </div>
  )
}

function handleCompleted(
  data: CreateTourRequest,
  onComplete: (tourRequest: CreateTourRequest_submitTourRequest) => void,
) {
  if (!data.submitTourRequest) throw new Error('no tour request')

  onComplete(data.submitTourRequest)
}

function handleSubmit(
  createTourRequest: (params: any) => void,
  desiredWeeklyScheduleParams: IDesiredWeeklyScheduleParams,
  children: IChild[],
  selectedChildren: IChild[],
  childEditStates: IChildEditState[],
  school: RequestTourModal_school,
  openings: Moment[],
  setValidation: (validation: IValidation) => void,
  t: TFunction,
) {
  const validation = validate(
    desiredWeeklyScheduleParams,
    children,
    selectedChildren,
    childEditStates,
    t,
  )

  if (isEmpty(validation)) {
    const desiredWeeklyScheduleVariables = {
      dropoffAt: desiredWeeklyScheduleParams.dropoffAt as string,
      friday: desiredWeeklyScheduleParams.days.friday,
      monday: desiredWeeklyScheduleParams.days.monday,
      pickupAt: desiredWeeklyScheduleParams.pickupAt as string,
      thursday: desiredWeeklyScheduleParams.days.thursday,
      tuesday: desiredWeeklyScheduleParams.days.tuesday,
      wednesday: desiredWeeklyScheduleParams.days.wednesday,
    }

    createTourRequest({
      variables: {
        desiredWeeklySchedule: desiredWeeklyScheduleVariables,
        tourRequest: {
          availabilities: openings.map(opening => {
            return { beginsAt: opening.format() }
          }),
          childId: selectedChildren[0].id,
          schoolId: school.id,
        },
      },
    })
  } else {
    setValidation(validation)
  }
}
